import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { chamSanSang } from "../tools/readiness/hoSoSanSang.js";

/**
 * Thẻ điểm mẫu ở đầu trang chủ (27/10/2026, học từ hero của FundingFactory):
 * thay vì ảnh minh hoạ, cho người mới thấy ngay thứ họ sẽ mang về.
 *
 * Điểm được chấm THẬT bằng đúng engine của công cụ Điểm sẵn sàng, trên một
 * startup hư cấu ("Sổ Chợ"). Nhờ vậy con số, trọng số và việc "Sửa tiếp theo"
 * trên thẻ luôn khớp với công cụ, kể cả khi sau này đổi rubric. Không viết
 * điểm cứng ở đây.
 *
 * HTML prerender là trạng thái cuối (đọc được khi chưa có JavaScript). Khi
 * thẻ lọt vào màn hình, điểm đếm lên và các thanh chạy một lần. Người bật
 * "giảm chuyển động" thấy trạng thái đứng yên.
 */
const VI_DU = {
  founders: [
    { key: "a", fullTime: true, domainYears: 6, hasVesting: false },
    { key: "b", fullTime: true, domainYears: 2, hasVesting: false },
  ],
  bangChung: ["PILOT", "WAITLIST", "LOI"],
  f: {
    entityType: "JSC",
    oneLiner: "Sổ công nợ trên điện thoại cho tiệm tạp hoá",
    problem: "Chủ tiệm tạp hoá ghi nợ khách vào sổ tay, cuối tháng không đối chiếu được nên thất thoát tiền.",
    productStage: "MVP",
    scaleLevel: 1,
    moatLevel: 1,
    somTy: 40,
    marketMethod: "BOTTOM_UP",
    channelLevel: 2,
    targetTy: 3,
    useOfFundsCount: 3,
    ipAssignment: "NONE",
    runwayMonths: 7,
  },
};

const KET_QUA = chamSanSang(VI_DU);
const TONG_TRONG_SO = KET_QUA.axes.reduce((a, t) => a + t.weight, 0);
const R = 34;
const CHU_VI = 2 * Math.PI * R;

export default function TheDiemMau({ to }) {
  const goc = useRef(null);
  // 1 = trạng thái cuối. Chỉ lùi về 0 rồi chạy lên khi có JavaScript và
  // người dùng không tắt chuyển động.
  const [tien, setTien] = useState(1);

  useEffect(() => {
    const el = goc.current;
    if (!el || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) return;
    setTien(0);
    let raf = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const batDau = performance.now();
      const chay = (t) => {
        const x = Math.min(1, (t - batDau) / 1400);
        setTien(1 - Math.pow(1 - x, 3));
        if (x < 1) raf = requestAnimationFrame(chay);
      };
      raf = requestAnimationFrame(chay);
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  const r = KET_QUA;
  const viec = r.nextBest;
  return (
    <figure className="tdm" ref={goc} aria-label="Ví dụ kết quả công cụ Điểm sẵn sàng gọi vốn">
      <div className="tdm-dau">
        <div>
          <p className="tdm-ten">Sổ Chợ <span>startup ví dụ</span></p>
          <p className="tdm-phu">Ứng dụng công nợ cho tiệm tạp hoá, gọi 3 tỷ vòng pre-seed</p>
        </div>
        <div className="tdm-vong" aria-hidden="true">
          <svg viewBox="0 0 80 80">
            <circle cx="40" cy="40" r={R} className="tdm-vong-nen" />
            <circle
              cx="40" cy="40" r={R} className="tdm-vong-muc"
              strokeDasharray={`${(CHU_VI * r.score * tien) / 100} ${CHU_VI}`}
              transform="rotate(-90 40 40)"
            />
          </svg>
          <b>{Math.round(r.score * tien)}</b>
        </div>
      </div>
      <p className="sr-only">Điểm sẵn sàng {r.score}/100, {r.bandLabel}.</p>
      <p className="tdm-muc">{r.bandLabel}</p>

      <ul className="tdm-truc">
        {r.axes.map((t) => {
          const tran = Math.round((t.weight / TONG_TRONG_SO) * 100);
          return (
            <li key={t.key}>
              <span>{t.label}</span>
              <span className="tdm-thanh" aria-hidden="true"><i style={{ width: `${(t.pct ?? 0) * tien}%` }} /></span>
              <b>{Math.round(t.contribution)}<small>/{tran}</small></b>
            </li>
          );
        })}
      </ul>

      {viec && (
        <div className="tdm-sua">
          <span className="tdm-sua-nhan">Sửa tiếp theo</span>
          <strong>{viec.label}</strong>
          <span className="tdm-sua-diem">+{viec.pointsAvailable} điểm, lên {viec.scoreAfter}</span>
        </div>
      )}

      <figcaption className="tdm-chan">
        <span>Chấm bằng đúng công cụ trên site. Số liệu là ví dụ minh hoạ.</span>
        {to && <Link to={to}>Chấm hồ sơ của bạn →</Link>}
      </figcaption>
    </figure>
  );
}
